import React from 'react';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {Plugins, Atom} from '@packages/systems/wfdl';
import type {AtomRenderProps} from '@packages/systems/wfdl';
import {
  FormCustomBooleanInput as FormCustomRadioInput,
  customControlBaseStyle,
  customControlFocusStyle,
} from './CustomControls';
import {
  setAllBordersRadius,
  setAllBordersColor,
  setAllBordersWidth,
} from './utils';
import {percent} from '@packages/systems/core/utils/WebflowCssSubset';
import {createIntegratedRootAliasInstance} from '@packages/utilities/integrated-types';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {getInPojo, getString} from '@packages/utilities/data-utils';

const FormRadioInput = ({
  attributes,
  classes,
  generateAttributes,
}: AtomRenderProps) => <input {...generateAttributes(attributes, classes)} />;

const render = Atom.createRender(({classes, ...props}) => {
  const buttonClasses = classes.push('w-radio-input');
  const runtimeEnvironment = useRuntimeEnvironment();

  // We always want the standard input in the LLM environment.
  const renderCustomControl =
    getString(getInPojo(props.data, ['inputType'])) === 'custom' &&
    runtimeEnvironment !== WebflowEnvironments.LLM;

  return renderCustomControl ? (
    <FormCustomRadioInput
      isCheckbox={false}
      classes={buttonClasses}
      {...props}
    />
  ) : (
    <FormRadioInput classes={buttonClasses} {...props} />
  );
});

const config = Atom.create({
  displayName: 'Radio Button',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('Radio')),
  },
  showEmpty: false,
  constraints: {
    pinToParent: true,
  },
  render,
  style: {
    // StyleRuleUtils work like expected without empty base, but
    // flow annotation for style requires base
    base: {
      main: {
        noPseudo: {},
      },
    },
    conditional: {
      inputType: {
        custom: {
          // Technically this could be fixed by updating the
          // `AtomStyleDeclaration` type to allow
          // `$Values<typeof REDIRECTED_PSEUDOS>` in addition to
          // `PseudoStateKey`s, however since `FormRadio/CheckboxInput`
          // is the only atom that actually supports these we're just
          // ignoring the error here.
          //

          main: {
            noPseudo: {
              ...customControlBaseStyle,
              ...setAllBordersRadius(percent(50)),
            },
            // @ts-expect-error - TS2322 - Type '{ noPseudo: { borderBottomLeftRadius: string; borderBottomRightRadius: string; borderTopLeftRadius: string; borderTopRightRadius: string; width: string; height: string; borderTopStyle: "none" | ... 2 more ... | "solid"; ... 10 more ...; borderRightWidth: string; }; 'redirected-focus': { ...; }; 'redirected-checked':...' is not assignable to type 'Partial<Record<"lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within", WebflowCssProperties>>'.
            'redirected-focus': {
              ...customControlFocusStyle,
            },
            'redirected-checked': {
              ...setAllBordersWidth(4),
              ...setAllBordersColor({r: 56, g: 152, b: 236, a: 1}),
            },
          },
        },
      },
    },
  },
  dataType: createIntegratedRootAliasInstance(
    qualify(Plugins.name('Form'), typeName('FormRadio'))
  ),
  hideAncestorSettings: true,
});

export default config;
